/* eslint-disable @typescript-eslint/no-explicit-any */
import React, {ReactNode, useEffect, useRef, useState} from 'react';
import {GetServerSidePropsContext} from 'next';
import {getPageData} from '../../[[...slug]]/index.page';
import {usePageSetting} from '~/providers/page-context.provider';
import {useRouter} from 'next/router';
import {Box} from '@mui/material';
import EStoreProductListDLC from '@wearesection/bandai-react-components/organisms/EStoreProductListDLC';
import productsApi from '~/pages/api/services/products-api';
import {PLACEHOLDER_IMG} from '~/constants';
import {convertToSlug, handleConvertToLocaleContentful, handleRenderPrice} from '~/utils';
import RenderNextLink from '~/components/wrapper/atoms/render-next-link';
import {handleFormatValuePlatform} from '../games/[slug]/index.page';

const ProductListDlc = () => {
  const route = useRouter();
  const {query, locale} = route;
  const {globalData} = usePageSetting();
  const dataTranslation = globalData?.dataTranslation?.[0] ?? {};
  const [productList, setProductList] = useState<any[]>([]);
  const [hasNextPage, setHasNextPage] = useState<boolean>(true);

  const [endCursor, setEndCursor] = useState<any>('');

  const refTimeOut = useRef<any>();

  const handleFormatData = (data: any[]) => {
    if (!data?.length) return [];

    return data.map((product: any) => {
      return {
        id: product?.id,
        title: product?.title,
        mainImage: product?.mainImage ?? PLACEHOLDER_IMG,
        description: '',
        platformList: !product?.platforms?.length
          ? []
          : product?.platforms.map((item: any) => {
              const title = handleFormatValuePlatform(item?.title);
              return {
                titleIcon: title,
                value: title,
                id: item?.id,
              };
            }),
        linkHref: `/store/games/${convertToSlug(product?.title || '')}`,
        buttonProps: {
          variant: 'secondary',
          label: dataTranslation['game-detailed-sub-nav-buy'] || 'Buy',
        },
        priceProps: {
          label: dataTranslation['product-from'] || 'From',
          currency: '',
          amount: handleRenderPrice(product?.price || '00', null, locale),
          noFormat: true,
        },
        isButtonSecond: true,
        nextLink: (href: string, child: ReactNode) => (
          <RenderNextLink href={`${href}`} child={child} setting={{scroll: true}} />
        ),
      };
    });
  };

  useEffect(() => {
    if (typeof window !== 'undefined') {
      refTimeOut.current = window.scrollTo({top: 10});
    }
    return () => {
      window.clearTimeout(refTimeOut.current);
    };
  }, []);

  useEffect(() => {
    if (Object.keys(dataTranslation)?.length) {
      productsApi
        .getListProducts(
          {
            sort: 'NEWEST',
            type: 31,
          },
          handleConvertToLocaleContentful(locale as string)
        )
        .then((res: any) => {
          if (res?.status !== 200) {
            throw res;
          }
          const {data, meta} = res.data;
          const newData: any[] = handleFormatData(data);
          setProductList(newData);
          setEndCursor(meta?.endCursor || '');
          setHasNextPage(meta?.hasNextPage || '');
        })
        .catch(() => {
          route.push(`/store/games/${(query?.title as string) || ''}`);
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query?.title, dataTranslation]);

  const handleLoaMore = () => {
    if (!productList?.length) return;
    productsApi
      .getListProducts(
        {
          sort: 'NEWEST',
          type: 31,
          endCursor: endCursor,
        },
        handleConvertToLocaleContentful(locale as string)
      )
      .then((res: any) => {
        if (res?.status !== 200) {
          throw res;
        }
        const {data, meta} = res.data;
        const newData: any[] = handleFormatData(data);
        setProductList([...productList, ...newData]);
        setEndCursor(meta?.endCursor || '');
        setHasNextPage(meta?.hasNextPage || '');
      })
      .catch(() => {
        route.push(`/store/games/${convertToSlug((query?.title as string) || '')}`);
      });
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
      }}
    >
      <EStoreProductListDLC
        productList={productList}
        onLoadMore={handleLoaMore}
        onBack={() =>
          route.replace(`/store/games/${convertToSlug((query?.tb as string) || '')}?to=dlc`)
        }
        onClickBuy={(value: any) => {
          if (typeof window !== 'undefined') {
            window.open(
              `/store/games/${convertToSlug(`${value.id}`)}?platform=${value?.idPlatform}`
            );
          }
        }}
        hasNextPage={hasNextPage}
        title={dataTranslation['estore-products-detail-title-dowloadable-dlc']}
        titleModalSelectPlatform={
          dataTranslation['estore-products-detail-title-dowloadable-select-platform']
        }
        titleBack={`${dataTranslation['estore-product-dlc-back-to'] || ''} ${
          (query?.tb as string) || ''
        }`}
        titleButtonBuyNowDigital={{
          ps:
            dataTranslation['estore-products-detail-title-button-buy-now-ps'] ||
            'Buy now at PS Store',
          stream:
            dataTranslation['estore-products-detail-title-button-buy-now-steam'] ||
            'Buy now at Steam',
          xbox:
            dataTranslation['estore-products-detail-title-button-buy-now-xbox'] ||
            'Buy now at Xbox.com',
          nintendo:
            dataTranslation['estore-products-detail-title-button-buy-now-nintendo'] ||
            'Buy now at Nintendo.com',
        }}
      />
    </Box>
  );
};

export const getStaticProps = async (ctx: GetServerSidePropsContext) => {
  const preview = !!(process && process.env && process.env.NEXT_PUBLIC_PREVIEW);

  const promises: [ReturnType<typeof getPageData>] = [getPageData({slug: 'store', preview}, ctx)];
  const [pageData] = await Promise.all(promises);
  const {data} = pageData;
  return {
    props: {
      data,
    },
  };
};

export default ProductListDlc;
